import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  PoundSterling, FileText, Calendar, CheckCircle, Clock, AlertTriangle,
  Download, RefreshCw, Building2, Search, Loader2, Receipt, 
  ArrowUpRight, Upload, Filter
} from "lucide-react";
import { API } from "@/App";

export const AccountsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("deductions");
  
  // Data
  const [pendingDeductions, setPendingDeductions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  
  // Filters
  const [monthFilter, setMonthFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Modals
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedDeduction, setSelectedDeduction] = useState(null);
  const [statementRef, setStatementRef] = useState("");
  const [applying, setApplying] = useState(false);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === "deductions") {
        const res = await axios.get(`${API}/accounts/deductions/pending`, { headers });
        setPendingDeductions(res.data.deductions || []);
      } else if (activeTab === "invoices") {
        const res = await axios.get(`${API}/accounts/invoices/for-xero`, { headers });
        setInvoices(res.data.invoices || []);
      } else if (activeTab === "receipts") {
        const res = await axios.get(`${API}/accounts/receipts`, { headers });
        setReceipts(res.data.receipts || []);
      } else if (activeTab === "audit") {
        const res = await axios.get(`${API}/accounts/audit-logs?limit=100`, { headers });
        setAuditLogs(res.data.logs || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDeduction = async () => {
    if (!selectedDeduction || !statementRef) {
      toast.error("Please enter a statement reference");
      return;
    }
    
    setApplying(true);
    try {
      await axios.post(
        `${API}/accounts/deductions/${selectedDeduction.deduction_id}/apply`,
        { statement_reference: statementRef },
        { headers }
      );
      toast.success("Deduction applied to statement");
      setShowApplyModal(false);
      setSelectedDeduction(null);
      setStatementRef("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply deduction");
    } finally {
      setApplying(false);
    }
  };

  const handleMarkSynced = async (invoiceId) => {
    const xeroId = prompt("Enter Xero Invoice ID:");
    if (!xeroId) return;
    
    try {
      await axios.post(
        `${API}/accounts/invoices/${invoiceId}/mark-synced`,
        { xero_invoice_id: xeroId },
        { headers }
      );
      toast.success("Invoice marked as synced");
      fetchData();
    } catch (error) {
      toast.error("Failed to mark as synced");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case "applied":
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Applied</Badge>;
      case "submitted":
        return <Badge className="bg-blue-100 text-blue-700"><FileText className="w-3 h-3 mr-1" /> Submitted</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredDeductions = pendingDeductions.filter(d => {
    if (monthFilter && d.target_month !== monthFilter) return false;
    if (searchTerm && !d.property_address?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Accounts & Invoicing</h1>
            <p className="text-slate-500">Manage deductions, invoices, and receipts</p>
          </div>
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Deductions</p>
                  <p className="text-2xl font-bold">{pendingDeductions.length}</p>
                </div>
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Pending</p>
                  <p className="text-2xl font-bold">
                    £{pendingDeductions.reduce((sum, d) => sum + (d.total_amount || 0), 0).toFixed(2)}
                  </p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <PoundSterling className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Unsynced Invoices</p>
                  <p className="text-2xl font-bold">{invoices.length}</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Receipts</p>
                  <p className="text-2xl font-bold">{receipts.length}</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="deductions">
              <Calendar className="w-4 h-4 mr-2" />
              Pending Deductions
            </TabsTrigger>
            <TabsTrigger value="invoices">
              <FileText className="w-4 h-4 mr-2" />
              Invoices (Xero)
            </TabsTrigger>
            <TabsTrigger value="receipts">
              <Receipt className="w-4 h-4 mr-2" />
              Receipts
            </TabsTrigger>
            <TabsTrigger value="audit">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Audit Log
            </TabsTrigger>
          </TabsList>

          {/* Pending Deductions Tab */}
          <TabsContent value="deductions">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Pending Rent Deductions</CardTitle>
                    <CardDescription>Deductions waiting to be applied to landlord statements</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Filter by month (YYYY-MM)"
                      value={monthFilter}
                      onChange={(e) => setMonthFilter(e.target.value)}
                      className="w-40"
                    />
                    <Input
                      placeholder="Search property..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-48"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : filteredDeductions.length === 0 ? (
                  <div className="text-center py-10 text-slate-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
                    <p>No pending deductions</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Property</TableHead>
                        <TableHead>Issue Type</TableHead>
                        <TableHead>Contractor</TableHead>
                        <TableHead className="text-right">Mgmt Fee</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead>Target Month</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredDeductions.map((ded) => (
                        <TableRow key={ded.deduction_id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-slate-400" />
                              <span className="font-medium">{ded.property_address}</span>
                            </div>
                          </TableCell>
                          <TableCell>{ded.issue_type || "Maintenance"}</TableCell>
                          <TableCell>£{ded.contractor_amount?.toFixed(2)}</TableCell>
                          <TableCell className="text-right text-blue-600">
                            £{ded.management_fee?.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right font-bold">
                            £{ded.total_amount?.toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{ded.target_month}</Badge>
                          </TableCell>
                          <TableCell>{getStatusBadge(ded.status)}</TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedDeduction(ded);
                                setShowApplyModal(true);
                              }}
                            >
                              Apply
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab (Xero) */}
          <TabsContent value="invoices">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Contractor Invoices</CardTitle>
                    <CardDescription>Invoices ready to sync with Xero</CardDescription>
                  </div>
                  <Button variant="outline">
                    <ArrowUpRight className="w-4 h-4 mr-2" />
                    Export for Xero
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="text-center py-10 text-slate-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
                    <p>All invoices synced</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Contractor</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">VAT</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map((inv) => (
                        <TableRow key={inv.invoice_id}>
                          <TableCell className="font-mono">{inv.invoice_number}</TableCell>
                          <TableCell>{inv.contact_name}</TableCell>
                          <TableCell className="max-w-xs truncate">
                            {inv.line_items?.[0]?.description || "Maintenance"}
                          </TableCell>
                          <TableCell>{inv.date}</TableCell>
                          <TableCell className="text-right">£{inv.sub_total?.toFixed(2)}</TableCell>
                          <TableCell className="text-right">£{inv.total_tax?.toFixed(2)}</TableCell>
                          <TableCell className="text-right font-bold">£{inv.total?.toFixed(2)}</TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMarkSynced(inv.invoice_id)}
                            >
                              Mark Synced
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Receipts Tab */}
          <TabsContent value="receipts">
            <Card>
              <CardHeader>
                <CardTitle>Payment Receipts</CardTitle>
                <CardDescription>Receipts for landlord payments via Stripe</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : receipts.length === 0 ? (
                  <div className="text-center py-10 text-slate-500">
                    <Receipt className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No receipts yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Receipt ID</TableHead>
                        <TableHead>Job</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Payment ID</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {receipts.map((receipt) => (
                        <TableRow key={receipt.receipt_id}>
                          <TableCell className="font-mono">{receipt.receipt_id}</TableCell>
                          <TableCell>{receipt.job_id}</TableCell>
                          <TableCell className="font-bold">£{receipt.amount?.toFixed(2)}</TableCell>
                          <TableCell>{new Date(receipt.created_at).toLocaleDateString()}</TableCell>
                          <TableCell className="text-xs font-mono text-slate-500">
                            {receipt.stripe_payment_intent?.substring(0, 20)}...
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit">
            <Card>
              <CardHeader>
                <CardTitle>Audit Log</CardTitle>
                <CardDescription>Track all account-related events</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : auditLogs.length === 0 ? (
                  <div className="text-center py-10 text-slate-500">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No audit events</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {auditLogs.map((log) => (
                      <div key={log.audit_id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          log.event_type.includes("blocked") ? "bg-red-500" :
                          log.event_type.includes("completed") ? "bg-green-500" :
                          "bg-blue-500"
                        }`} />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{log.event_type.replace(/_/g, " ").toUpperCase()}</p>
                          <p className="text-xs text-slate-500">
                            {log.user_role} • {new Date(log.timestamp).toLocaleString()}
                          </p>
                          {log.job_id && (
                            <p className="text-xs text-slate-400 mt-1">Job: {log.job_id}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Apply Deduction Modal */}
        <Dialog open={showApplyModal} onOpenChange={setShowApplyModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Apply Deduction to Statement</DialogTitle>
              <DialogDescription>
                Mark this deduction as applied to a landlord rent statement
              </DialogDescription>
            </DialogHeader>
            
            {selectedDeduction && (
              <div className="space-y-4 py-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-500">Property</p>
                  <p className="font-medium">{selectedDeduction.property_address}</p>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-slate-50 p-3 rounded-lg text-center">
                    <p className="text-xs text-slate-500">Contractor</p>
                    <p className="font-bold">£{selectedDeduction.contractor_amount?.toFixed(2)}</p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <p className="text-xs text-blue-600">Mgmt Fee (10%)</p>
                    <p className="font-bold text-blue-700">£{selectedDeduction.management_fee?.toFixed(2)}</p>
                  </div>
                  <div className="bg-emerald-50 p-3 rounded-lg text-center">
                    <p className="text-xs text-emerald-600">Total</p>
                    <p className="font-bold text-emerald-700">£{selectedDeduction.total_amount?.toFixed(2)}</p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium">Statement Reference *</label>
                  <Input
                    value={statementRef}
                    onChange={(e) => setStatementRef(e.target.value)}
                    placeholder="e.g. STMT-2025-01-001"
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApplyModal(false)}>Cancel</Button>
              <Button 
                onClick={handleApplyDeduction}
                disabled={applying || !statementRef}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {applying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Apply Deduction
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AccountsPage;
